/**
 * *"Sempre categorizar Uberrides como Transporte?"* — a barra que aparece
 * depois de categorizar à mão uma descrição que a tabela ainda não conhece
 * (Fase 10, item 3).
 *
 * ## Perguntar, e não aprender em silêncio
 *
 * Foi a escolha do dono, contra a alternativa de criar a regra sozinha a cada
 * categorização. O argumento dele vale: uma regra que nasce calada só é
 * descoberta depois de já ter categorizado alguma coisa errada — e a tabela
 * cresce com decisões que ninguém tomou de propósito.
 *
 * ## Silêncio NÃO cria regra
 *
 * Fechar, trocar de tela ou simplesmente não responder deixa tudo como estava:
 * a categoria vale para aquela transação, e a pergunta volta na próxima. É o
 * que separa "perguntar" de "aprender sozinho" — se ignorar virasse "sim", a
 * escolha do dono teria sido desfeita na implementação.
 *
 * ## Uma por vez, e sem bloquear
 *
 * A barra não é diálogo: durante a triagem categoriza-se cinco seguidas, e um
 * modal por escolha seria cinco interrupções. A última pergunta substitui a
 * anterior — duas barras empilhadas cobririam a lista que se está varrendo.
 *
 * O lugar e a forma são os do aviso de nova versão: acima da barra de abas, em
 * pílula, com o texto encurtando antes dos botões.
 */
export type PerguntaDeRegraProps = {
  /** `null` = nada a perguntar agora. */
  pergunta: { descricao: string; categoria: string } | null
  onSempre: () => void
  onNunca: () => void
  onFechar: () => void
}

export function PerguntaDeRegra({ pergunta, onSempre, onNunca, onFechar }: PerguntaDeRegraProps) {
  if (pergunta === null) return null

  return (
    <div
      role="status"
      className="flutuante fixed inset-x-0 bottom-0 z-40 mx-auto w-full max-w-md px-4 pb-[calc(env(safe-area-inset-bottom)+5.5rem)]"
    >
      <div className="flex items-center gap-2 rounded-2xl border border-[var(--border-glass)] bg-[var(--surface)] p-2 pl-3 shadow-2xl">
        {/* Duas linhas no máximo: `Transferência enviada|MINISTERIO DA
            FAZENDA` sozinho ocupa três, e a barra cresce por cima da lista. */}
        <p className="line-clamp-2 min-w-0 flex-1 text-xs">
          {`Sempre categorizar “${pergunta.descricao}” como ${pergunta.categoria}?`}
        </p>
        <button
          type="button"
          onClick={onSempre}
          className="alvo shrink-0 rounded-full px-3 text-xs font-medium text-[var(--brand)]"
        >
          Sempre
        </button>
        {/* "Nunca" não é o oposto de "Sempre" — é a terceira resposta, e a que
            cala a pergunta para uma descrição que muda de natureza todo mês. */}
        <button
          type="button"
          onClick={onNunca}
          className="alvo shrink-0 rounded-full px-3 text-xs text-[var(--text-muted)]"
        >
          Nunca
        </button>
        <button
          type="button"
          onClick={onFechar}
          aria-label="Agora não"
          title="Agora não"
          className="alvo shrink-0 text-[var(--text-faint)]"
        >
          ✕
        </button>
      </div>
    </div>
  )
}
